import { useEffect, useRef } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BASE_API_URL } from "store/Me";
import { SITE_SYNC_RECORDS_QUERY_KEY } from "./siteQueryKeys";
import { siteKeysQueryKey } from "./siteKeysApi";

/** Sync record statuses while the queued operations haven't finished. */
const IN_PROGRESS = new Set(["Requested", "Processing"]);

export const SYNC_POLL_INTERVAL_MS = 5000;

interface LatestSyncRecord {
  id: string;
  status: string;
}

export const latestSiteSyncRecordQueryKey = (siteId: string) =>
  [SITE_SYNC_RECORDS_QUERY_KEY, siteId, "latest"] as const;

async function getLatestSiteSyncRecord(siteId: string): Promise<LatestSyncRecord | null> {
  // Sync records come newest first.
  const result = await axios.get<LatestSyncRecord[]>(
    `${BASE_API_URL}/api/Sites/${siteId}/sync-records?page=0&pageSize=1`,
  );
  return Array.isArray(result.data) ? (result.data[0] ?? null) : null;
}

export function isSyncInProgress(status: string | null | undefined): boolean {
  return !!status && IN_PROGRESS.has(status);
}

/**
 * Watches the site's newest sync record. While it's Requested or Processing (a sync was just
 * queued, or one was already running when the page loaded) it's polled; when it finishes, the
 * site's key views, sync records, details and summary are refreshed, so e.g. a Fobs sync's results
 * show without a reload. Polling stops when it finishes and when the page unmounts.
 *
 * Call `watchNewSync()` after queueing a sync to pick up its record straight away.
 */
export function useSiteSyncWatcher(
  siteId: string,
  { intervalMs = SYNC_POLL_INTERVAL_MS }: { intervalMs?: number } = {},
) {
  const queryClient = useQueryClient();
  const { data: latest } = useQuery({
    queryKey: latestSiteSyncRecordQueryKey(siteId),
    queryFn: () => getLatestSiteSyncRecord(siteId),
    refetchInterval: (query) =>
      isSyncInProgress(query.state.data?.status) ? intervalMs : false,
  });

  const previous = useRef<LatestSyncRecord | null | undefined>(undefined);
  useEffect(() => {
    const was = previous.current;
    previous.current = latest;
    if (latest === undefined) return;
    if (was && isSyncInProgress(was.status) && !isSyncInProgress(latest?.status)) {
      queryClient.invalidateQueries({ queryKey: siteKeysQueryKey(siteId) });
      queryClient.invalidateQueries({ queryKey: [SITE_SYNC_RECORDS_QUERY_KEY, siteId] });
      queryClient.invalidateQueries({ queryKey: ["sites", "details", siteId] });
      queryClient.invalidateQueries({ queryKey: ["sites", "summary", siteId] });
    }
  }, [latest, queryClient, siteId]);

  return {
    latestStatus: latest?.status,
    watchNewSync: () =>
      queryClient.invalidateQueries({ queryKey: [SITE_SYNC_RECORDS_QUERY_KEY, siteId] }),
  };
}
